import React, { useCallback, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { useHistory } from 'react-router';
import { Button, Typography, Card, CardActionArea, Snackbar } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import { defaultProviderInfoState } from '@apps/shared/src/addCase/reducer';
import { getAxiosRequestConfig } from '@apps/shared/src/utilities';
import { defaultMemberState } from '@apps/shared/src/getMember/reducer';
import BalanceBill from './BalanceBill';
import CallAhead from './CallAhead';
import Appeal from './Appeal';
import AccessIssue from './AccessIssue';
import LoaderDialog from '../shared/loaderDialog';
import SuccessDialog from '../shared/successDialog';
import ReCaptchaComponent from './ReCaptchaComponent';

const useStyles = makeStyles(() => ({
  container: {
    padding: 20,
    backgroundColor: '#f7f7f7',
    margin: 'auto',
  },
  formContainer: {
    padding: '1rem',
    margin: '1rem',
    backgroundColor: '#ECF0F2',
  },
  logo: {
    padding: '1rem 2rem .5rem 2rem',
    borderTop: '1px solid #F97240',
  },
  header: {
    padding: '1rem 2rem',
    background: 'linear-gradient(to right, #EC7625, #f9c275ff)',
    margin: '.5rem .7rem',
    color: 'white',
  },
  subTitle: {
    padding: '1.5rem 0 .8rem 1rem',
  },
  cardContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  card: {
    display: 'flex',
    flex: 1,
    margin: 10,
    padding: '1rem .5rem',
    border: '1px solid #FAC8B3',
    background: 'white',
    borderRadius: '.5rem',
    '&.selected': {
      background: '#EC7625',
      color: 'white',
    },
  },
  cardContent: {
    padding: 16,
    textAlign: 'center',
  },

  gutter: {
    margin: '1rem',
  },
  desc: {
    marginTop: '.5rem',
  },
  icon: {
    fontSize: 40,
    color: '#f26522',
  },
  button: {
    marginTop: 20,
    padding: '.5rem 1.5rem',
    backgroundColor: '#f26522',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#d35400',
    },
  },
  submitSection: {
    margin: '0 1rem',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgba(255, 255, 255, 0)', // Initial transparent background
    zIndex: -1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0, // Initial opacity set to 0
    transition: 'background 0.3s ease, opacity 0.3s ease', // Transition properties
  },
  overlayVisible: {
    background: 'rgba(255, 255, 255, 0.8)', // Visible background color
    opacity: 1, // Visible opacity
    zIndex: 999,
  },
}));

const FORM_TYPES = [
  {
    label: 'Balance Bill',
    description:
      'A Balance Bill occurs when a provider believes that additional funds are owed in addition to reimbursement already provided by the insurance plan.',
    selectedImg: './images/RBP_BB.svg',
    img: './images/RBP_BB_Plain.svg',
  },
  {
    label: 'Access Issue',
    description:
      'An Access Issue is when a member has tried to schedule an appointment with a provider, and they have declined the insurance.',
    selectedImg: './images/RBP_AI.svg',
    img: './images/RBP_AI_Plain.svg',
  },
  {
    label: 'Appeal',
    description: '',
    selectedImg: './images/RBP_AP.svg',
    img: './images/RBP_AP_Plain.svg',
  },
  {
    label: 'Call Ahead',
    description:
      'Call Ahead is for those making a first-time appointment with a physician when starting out on this health plan.',
    selectedImg: './images/RBP_CA.svg',
    img: './images/RBP_CA_Plain.svg',
  },
];

const createDefaultAddCaseState = () => {
  const memberInfo = JSON.parse(JSON.stringify(defaultMemberState.memberInfo));
  const providerInfo = JSON.parse(JSON.stringify(defaultProviderInfoState));

  // Ensure newPatient is empty instead of boolean
  memberInfo.newPatient = '';

  return {
    memberInfo,
    providerInfo,
  };
};

const reasonForAccessIssue = [
  'Patient tried to make appointment, was denied',
  'Patient had appointment scheduled, Facility/Professional called to cancel',
  'Facility/Professional requires payment up front/self-pay',
  'Facility/Professional requires agreement/contract to see patient',
  'Other',
];

const RBPForm: React.FC = () => {
  const classes = useStyles();
  const [formType, setFormType] = useState('Balance Bill');
  const [formData, setFormData] = useState(createDefaultAddCaseState);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [token, setToken] = useState<string>('');
  const [isCaseCreated, setIsCreated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [accessIssueReason, setAccessIssueReason] = useState<typeof reasonForAccessIssue>([]);
  const [accessIssueReasonOthers, setAccessIssueReasonOthers] = useState('');
  const [recaptchaKey, setRecaptchaKey] = useState(0);
  const history = useHistory();

  const renderFormFields = () => {
    switch (formType) {
      case 'Balance Bill':
        return (
          <BalanceBill
            formData={formData}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        );
      case 'Access Issue':
        return (
          <AccessIssue
            formData={formData}
            setFormData={setFormData}
            accessIssueReason={accessIssueReason}
            setAccessIssueReason={setAccessIssueReason}
            accessIssueReasonOthers={accessIssueReasonOthers}
            setAccessIssueReasonOthers={setAccessIssueReasonOthers}
          />
        );
      case 'Appeal':
        return (
          <Appeal
            formData={formData}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        );
      case 'Call Ahead':
        return (
          <CallAhead
            formData={formData}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        );
      default:
        return (
          <BalanceBill
            formData={formData}
            setFormData={setFormData}
            uploadedFiles={uploadedFiles}
            setUploadedFiles={setUploadedFiles}
          />
        );
    }
  };

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const caseDetail = { caseId: '', isCreated: '' };
      setIsCreated(false);

      if (!token) {
        alert('Please complete the reCAPTCHA.');
        return;
      }

      try {
        setLoading(true);

        const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
        const createCaseUrl = `${baseUrl}/api/createRbpFormCase`;
        const uploadUrl = `${baseUrl}/api/uploadAttachmentRbpForms`;

        // Determine recordType based on caseType
        let recordType: string;
        switch (formType) {
          case 'Balance Bill':
            recordType = 'BalanceBill';
            break;
          case 'Call Ahead':
            recordType = 'CallAhead';
            break;
          case 'Appeal':
            recordType = 'Appeal';
            break;
          default:
            recordType = 'AccessIssue';
        }

        // Extract newPatient conditionally for cases
        const { newPatient, ...memberInfoWithoutNewPatient } = formData.memberInfo;
        const memberInfo =
          recordType === 'AccessIssue'
            ? formData.memberInfo // keep newPatient
            : memberInfoWithoutNewPatient; // exclude newPatient

        // --- Combine accessIssueReason and accessIssueReasonOthers ---
        let providerInfo = { ...formData.providerInfo };
        if (recordType === 'AccessIssue') {
          const combinedReasons = [...accessIssueReason.filter(Boolean)].filter(Boolean).join(',');
          providerInfo = { ...providerInfo, reasonForAccessIssue: combinedReasons };
        }

        // Remove scheduledDateOfService if it's empty
        if (!formData.providerInfo.scheduledDateOfService) {
          delete providerInfo.scheduledDateOfService;
        }

        // Prepare payload
        const payload =
          recordType === 'BalanceBill' || recordType === 'Appeal'
            ? {
                ...memberInfo,
                description: { ...formData.providerInfo.description },
                token,
                recordType,
              }
            : {
                ...memberInfo,
                ...providerInfo,
                token,
                recordType,
              };

        // Create case
        const result = await axios.post(createCaseUrl, payload, getAxiosRequestConfig());

        const newCaseId = result.data.id;
        caseDetail.caseId = newCaseId;

        // Upload files if any
        if (uploadedFiles.length > 0) {
          try {
            const formData = new FormData();
            formData.append('caseID', newCaseId);
            formData.append('contentLocation', '');
            formData.append('title', newCaseId);

            uploadedFiles.forEach(file => formData.append('files', file, file.name));

            const uploadResult = await axios.post(uploadUrl, formData, getAxiosRequestConfig());
            caseDetail.isCreated = uploadResult.data.success;
          } catch (error) {
            if ((error as AxiosError).response) {
              console.log(error);
            }
          }
        }

        setLoading(false);
        setIsCreated(true);
      } catch (error) {
        if ((error as AxiosError).response) {
          const axiosError = error as AxiosError;
          const errorResponse = axiosError.response?.data;
          setErrorMessage(errorResponse || 'Something went wrong. Please try again.');
          setShowError(true);
        }
        setLoading(false);
      }
    },
    [
      setLoading,
      setIsCreated,
      formData,
      formType,
      uploadedFiles,
      token,
      accessIssueReason,
      accessIssueReasonOthers,
    ]
  );

  const validEmail = (email: string): boolean => {
    if (email !== '') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidCharactersRegex = /[&!#^$%*()''""~,`]/;
      return emailRegex.test(email) && !invalidCharactersRegex.test(email);
    }
    return true;
  };

  const validPhoneNumber = (phoneNumber: string): boolean => {
    if (phoneNumber !== '') {
      return phoneNumber.length === 10;
    }
    return true;
  };

  const validZipCode = (zipCode: string): boolean => {
    if (zipCode !== '') {
      return zipCode.length === 5;
    }
    return true;
  };

  const handleTypeChange = (formType: string): void => {
    setFormData(createDefaultAddCaseState());
    setAccessIssueReason([]);
    setAccessIssueReasonOthers('');
    setFormType(formType);
    setUploadedFiles([]);
    setIsCreated(false);
  };

  const handleSuccess = (): void => {
    setFormData(createDefaultAddCaseState());
    setAccessIssueReasonOthers('');
    setAccessIssueReason([]);
    setFormType('Balance Bill');
    setUploadedFiles([]);
    setIsCreated(false);
    setRecaptchaKey(prev => prev + 1); // Force reCAPTCHA remount
    history.push('/rbp-form');
  };

  // ✅ strict date parser
  const parseStrictDate = (dateString: string): Date | null => {
    const regex = /^\d{4}-(\d{2})-(\d{2})$/;
    if (!regex.test(dateString)) return null;

    const [year, month, day] = dateString.split('-').map(Number);

    // Year: 1900–2100
    if (year < 1900 || year > 2100) return null;

    // Months: 1–12, Days: 1–31
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const date = new Date(year, month - 1, day);

    // Ensure constructed date matches parts (strict check)
    if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
      return null;
    }

    return date;
  };

  // ✅ service date validation
  const isValidServiceDate = (value: string): Boolean => {
    if (value) {
      const regex = /^\d{4}-\d{2}-\d{2}$/;
      if (regex.test(value)) return false;

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return false;

      const year = date.getUTCFullYear();
      if (year < 1900 || year > 2100) return false;

      return true;
    }
    return true;
  };

  // ✅ DOB validation
  const isValidDOB = (value: string): boolean => {
    if (!value) return false; // let "required" handle empty check

    // ✅ Strict YYYY-MM-DD with 4-digit year
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (regex.test(value)) {
      return false;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    const today = new Date();

    // ✅ Prevent future dates
    if (date > today) {
      return false;
    }

    // ✅ Prevent dates more than 200 years in the past
    const twoHundredYearsAgo = new Date(
      today.getFullYear() - 200,
      today.getMonth(),
      today.getDate()
    );
    if (date < twoHundredYearsAgo) {
      return false;
    }

    return true;
  };

  const disableSubmit = useMemo(() => {
    const isValidDate = isValidDOB(formData.memberInfo.dateOfBirth);
    const commonValidations =
      !token ||
      !formData.memberInfo ||
      !formData.memberInfo?.submitterContactInfo?.firstName ||
      !formData.memberInfo?.submitterContactInfo?.lastName ||
      !formData.memberInfo?.submitterContactInfo?.companyName ||
      !formData.memberInfo?.submitterContactInfo?.phoneNumber ||
      !validPhoneNumber(formData.memberInfo?.submitterContactInfo?.phoneNumber) ||
      !formData.memberInfo?.submitterContactInfo?.emailAddress ||
      !validEmail(formData.memberInfo?.submitterContactInfo?.emailAddress) ||
      !formData.memberInfo.memberId ||
      !formData.memberInfo.firstName ||
      !formData.memberInfo.lastName ||
      !formData.memberInfo.dateOfBirth ||
      !isValidDate ||
      !formData.memberInfo.phone ||
      !validPhoneNumber(formData.memberInfo.phone) ||
      !formData.memberInfo.email ||
      !validEmail(formData.memberInfo.email) ||
      !formData.memberInfo.address ||
      !formData.memberInfo.city ||
      !formData.memberInfo.state ||
      !formData.memberInfo.zip ||
      !validZipCode(formData.memberInfo.zip) ||
      !formData.memberInfo.groupid ||
      !formData.providerInfo?.description?.planname ||
      !formData.providerInfo?.description?.tpaName;

    if (formType === 'Balance Bill') {
      return commonValidations || uploadedFiles.length === 0;
    }

    if (formType === 'Appeal') {
      return (
        commonValidations ||
        uploadedFiles.length === 0 ||
        !formData.memberInfo?.submitterContactInfo?.oonAttachedForNSAClaim
      );
    }

    if (formType === 'Access Issue') {
      const type = formData.providerInfo?.typeOfAccessIssue;
      const isFacilityOrUnknown =
        type === 'Facility' || type === 'Professional - Physician Unknown';
      const isPhysicianOrUnknown =
        type === 'Physician' || type === 'Professional - Physician Unknown';

      const isValidSDate = isValidServiceDate(formData.providerInfo.scheduledDateOfService);

      const isValidReason = accessIssueReason.length > 0;

      const isValidReasonOverOther = formData.providerInfo.isReasonForAccessIssueOthers
        ? accessIssueReasonOthers.length > 0
        : true;

      const missingAccessIssueInfo =
        // required: Patient Type (boolean must be chosen)
        typeof formData.memberInfo.newPatient !== 'boolean' ||
        // required: Type of Access Issue
        !formData.providerInfo.typeOfAccessIssue ||
        // required: What is the patient being seen for?
        !formData.providerInfo.patientSeenFor ||
        // required: Reason for Access Issue
        !isValidReason ||
        !isValidReasonOverOther ||
        !isValidSDate ||
        // required: Provider Address block
        !formData.providerInfo.description.providerAddress ||
        !formData.providerInfo.description.providerCity ||
        !formData.providerInfo.description.providerState ||
        !formData.providerInfo.description.providerZip ||
        !validZipCode(formData.providerInfo.description.providerZip) ||
        // required: Provider Phone
        !formData.providerInfo.description.providerPhone ||
        !validPhoneNumber(formData.providerInfo.description.providerPhone) ||
        // required when Facility or Unknown
        (isFacilityOrUnknown &&
          (!formData.providerInfo.description.FacilityName ||
            !formData.providerInfo.description.BillingContact)) ||
        // required when Physician or Unknown
        (isPhysicianOrUnknown &&
          (!formData.providerInfo.description.physicianName ||
            !formData.providerInfo.description.physicianType));

      return commonValidations || missingAccessIssueInfo;
    }

    if (formType === 'Call Ahead') {
      const isValidDate = isValidServiceDate(formData.providerInfo.scheduledDateOfService);
      return (
        commonValidations ||
        !isValidDate ||
        !formData.providerInfo.description.physicianName ||
        !formData.providerInfo.description.providerOfficeName ||
        !formData.providerInfo.description.providerPhone ||
        !validPhoneNumber(formData.providerInfo.description.providerPhone) ||
        !formData.providerInfo.description.providerCity ||
        !formData.providerInfo.description.providerState ||
        !formData.providerInfo.description.providerZip ||
        !validZipCode(formData.providerInfo.description.providerZip)
      );
    }

    return false;
  }, [
    formData,
    token,
    formType,
    uploadedFiles,
    formData.memberInfo.dateOfBirth,
    formData.providerInfo.isReasonForAccessIssueOthers,
    accessIssueReason,
    accessIssueReasonOthers,
  ]);

  return (
    <>
      <div className={`${classes.overlay} ${loading ? classes.overlayVisible : ''}`}>
        <LoaderDialog
          open={loading}
          message="We're working on your request. Please hang tight, and we'll notify you once your case is submitted."
        />
        {isCaseCreated && (
          <SuccessDialog
            icon="images/success.svg"
            open={isCaseCreated}
            title="Submitted"
            message="Thanks for submitting the case form."
            buttonText="Continue"
            handleSuccess={handleSuccess}
          />
        )}
      </div>
      <Snackbar
        open={showError}
        autoHideDuration={3000}
        onClose={() => setShowError(false)}
        message={errorMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <div className={classes.container}>
        <div className={classes.logo}>
          <img
            src="./images/6DH_logo-standard.png"
            alt="6 Degrees Health"
            style={{ width: '20rem' }}
          />
        </div>

        <div className={classes.header}>
          <Typography variant="h4" style={{ fontWeight: 700 }}>
            RBP SUPPORT SERVICE FORMS
          </Typography>
        </div>
        <div style={{ padding: '0 2rem' }}>
          <div className={classes.subTitle}>
            <Typography>
              Please select the type of issue and fill the form below to submit a case.
            </Typography>
          </div>

          <div className={classes.cardContainer}>
            {FORM_TYPES.map(form => (
              <Card
                key={form.label}
                className={`${classes.card} ${formType === form.label ? 'selected' : ''}`}
                onClick={() => handleTypeChange(form.label)}
              >
                <CardActionArea className={classes.cardContent}>
                  {formType === form.label ? (
                    <img src={form.selectedImg} alt={form.label} />
                  ) : (
                    <img src={form.img} alt={form.label} />
                  )}
                  <Typography variant="h6" style={{ fontWeight: 700 }}>
                    {form.label}
                  </Typography>
                  <Typography variant="subtitle1" className={classes.desc}>
                    {form.description}
                  </Typography>
                </CardActionArea>
              </Card>
            ))}
          </div>
          <div className={classes.gutter}>
            <Typography variant="h4" gutterBottom>
              {formType} Form
            </Typography>
            {(formType === 'Balance Bill' || formType === 'Appeal') && (
              <>
                <Typography>
                  Please do not submit balance bills or appeals from multiple providers on the same
                  submission.
                </Typography>
                <Typography>
                  For non-access issue submissions, if you have not submitted the EOB or Balance
                  Bill/provider correspondence, please do so before continuing.
                </Typography>
                <Typography>
                  We cannot move forward in Balance Bill case negotiations without these documents.
                </Typography>
              </>
            )}
            {formType === 'Call Ahead' && (
              <>
                <Typography>
                  Is this the first time this patient is seeing this healthcare provider? If so,
                  complete this form and our team will call the healthcare provider prior to the
                  patient&apos;s 1st appointment to explain health plan coverage. This is ONLY for
                  first time office visits to physician groups. Hospital, Surgical Centers, Imaging
                  Centers and any other facility type providers are excluded from this program.
                </Typography>
                <Typography>
                  Members should have a previously scheduled appointment within the next 30 days or
                  need to schedule an appointment within the next two weeks.
                </Typography>
              </>
            )}
            {formType === 'Access Issue' && (
              <>
                <Typography>
                  Please do not submit access issues from multiple providers on the same submission.
                </Typography>
                <Typography>
                  What is an Access Issue? An Access Issue is when a member has tried to schedule an
                  appointment with a provider, and they have declined the insurance.
                </Typography>
              </>
            )}
          </div>

          <form className={classes.formContainer} onSubmit={handleSubmit}>
            {renderFormFields()}

            {/* eslint-disable react/self-closing-comp */}
            <div className={classes.submitSection}>
              <ReCaptchaComponent
                key={`${formType}-${recaptchaKey}`}
                sitekey="6LdIbvsrAAAAAE317_GFMoiHqo4YaXr5H-fhDCEk"
                setToken={setToken}
                action="case_add"
              />

              <Button
                className={classes.button}
                variant="contained"
                type="submit"
                disabled={disableSubmit}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default RBPForm;
